import { useCallback, useEffect, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { pagesToShowBanner } from "app/components/AddonsCart/AddonsCartConstants";
import { CookieManager } from "app/components/cookie-manager/CookieManager";
import { useDunnings } from "app/components/hooks/useDunnings";
import { useAddonsState } from "app/contexts/AddonStateContext/AddonStateContext";
import { ADDON_TYPE_NAME_DICT, AddOnType, type AddonTypeName } from "app/contexts/AddonStateContext/const";
import { useNoTouchPurchaseContext } from "app/contexts/NoTouchPurchaseContext/NoTouchPurchaseContext";
import useLocalStorage from "app/custom-hooks/useLocalStorage";
import { translate } from "app/filters/i18nFilter";
import { countriesMap } from "app/marketing/hooks/use-countries/country-map";
import { goToAddonsPage } from "app/UtilitiesAndConstants/UtilityFunctions/goToAddonsPage";
import { safeJsonParse } from "app/UtilitiesAndConstants/UtilityFunctions/safeJsonParse";

const ADDONS_CART_COOKIE_NAME = "addonsCart";

const { AdditionalDataCredits: _omitAdditionalDataCredits, SiDataCredits: _omitSiDataCredits, ...rest } = AddOnType;
const AddOnsCartId = { ...rest, DataCredits: "DataCreditsDynamic" } as const;

type AddOnsCartIdType = (typeof AddOnsCartId)[keyof typeof AddOnsCartId];

interface IAddonItem {
    id: AddOnsCartIdType;
    quantity: number;
    countryId?: number;
    countryAlpha2Code?: string;
    countryAlpha3Code?: string;
}

interface IUseAddonsCartApi {
    totalCartItems: number;
    cartItemsText: string[];
    hasAddonsCartBanner: boolean;
    isAddonsCartButtonEnabled: boolean;
    addonsFromCookies: string;
    onCartClick: () => void;
    onCloseBannerClick: () => void;
    removeAddonFromCart: (addonType: string) => void;
}

type AddonCart = string[] | undefined;

const localStorageKeyAddonsCartBannerDismissed = "TopBanner__IsAddonsCartDismissed";
const countryCodeLength = 3;
const countryFilterId = AddOnType.CountryFilter as AddOnsCartIdType;
const countryMapByAlpha3Code = new Map<string, { id: number; alpha2Code: string }>(
    countriesMap.map((country) => [country.Alpha3Code, { id: country.Id, alpha2Code: country.Alpha2Code }]),
);
const cartIdToAddonTypeNameMap: Record<AddOnsCartIdType, AddonTypeName> = {
    [AddOnsCartId.HistoricalData]: ADDON_TYPE_NAME_DICT.HistoricalDataDynamic,
    [AddOnsCartId.AdditionalUsers]: ADDON_TYPE_NAME_DICT.AdditionalUsersDynamic,
    [AddOnsCartId.AdditionalProjects]: ADDON_TYPE_NAME_DICT.AdditionalProjectsDynamic,
    [AddOnsCartId.DataCredits]: ADDON_TYPE_NAME_DICT.DataCreditsDynamic,
    [AddOnsCartId.SeoCampaigns]: ADDON_TYPE_NAME_DICT.SeoCampaignsDynamic,
    [AddOnsCartId.SeoKeywords]: ADDON_TYPE_NAME_DICT.SeoKeywordsDynamic,
    [AddOnsCartId.MonitorAndProtectCrawls]: ADDON_TYPE_NAME_DICT.MonitorAndProtectCrawls,
    [AddOnsCartId.CountryFilter]: ADDON_TYPE_NAME_DICT.CountryFilterDynamic,
};

export const useAddonsCart = (): IUseAddonsCartApi => {
    const { handlePurchaseAddonClick } = useNoTouchPurchaseContext();
    const [cartItems, setCartItems] = useState<IAddonItem[]>([]);
    const [addonsFromCookies, setAddonsFromCookies] = useState<string>("");
    const [isAddonsCartBannerDismissed, setIsAddonsCartBannerDismissed] = useLocalStorage(
        localStorageKeyAddonsCartBannerDismissed,
    );
    const { hasAddonsHub, canBuyAtLeastOneAddon } = useAddonsState();
    const isAddonsCartEnabled = hasAddonsHub && canBuyAtLeastOneAddon;

    const {
        state: { parent },
    } = useCurrentStateAndParams();
    const { hasDunnings } = useDunnings();

    const cartFromCookies = useMemo((): AddonCart => {
        const cookieManager = new CookieManager();
        return safeJsonParse(cookieManager.getCookie(ADDONS_CART_COOKIE_NAME) ?? undefined);
    }, []);

    useEffect(() => {
        if (!cartFromCookies || !isAddonsCartEnabled) {
            return;
        }

        setAddonsFromCookies(cartFromCookies.join(","));

        const parsedAddons: IAddonItem[] = cartFromCookies
            .map((addonCartId: string): IAddonItem | null => {
                if (addonCartId.startsWith(countryFilterId)) {
                    const alpha3Code = addonCartId.slice(-countryCodeLength);
                    const country = countryMapByAlpha3Code.get(alpha3Code);

                    if (!country) {
                        return null;
                    }

                    return {
                        id: countryFilterId,
                        quantity: 1,
                        countryId: country.id,
                        countryAlpha2Code: country.alpha2Code,
                        countryAlpha3Code: alpha3Code,
                    };
                }

                const id = addonCartId.replace(/\d+/g, "") as AddOnsCartIdType;
                const quantity = Number(addonCartId.match(/\d+/g)?.join(""));

                if (isNaN(quantity) || !Object.values(AddOnsCartId).includes(id)) {
                    return null;
                }

                return {
                    id: id,
                    quantity: quantity,
                };
            })
            .filter((item) => item !== null);

        setCartItems(parsedAddons);
    }, [cartFromCookies, isAddonsCartEnabled]);

    const cartItemsText: string[] = useMemo(() => {
        return cartItems.map((item) => {
            switch (item?.id) {
                case AddOnsCartId.HistoricalData:
                    return `${item.quantity} ${translate("addons.cart.tooltip.text.historicalData")}`;
                case AddOnsCartId.AdditionalUsers:
                    return `${item.quantity} ${translate("addons.cart.tooltip.text.seats")}`;
                case AddOnsCartId.AdditionalProjects:
                    return `${item.quantity} ${translate("addons.cart.tooltip.text.projects")}`;
                case AddOnsCartId.DataCredits:
                    return `${item.quantity} ${translate("addons.cart.tooltip.text.credits")}`;
                case AddOnsCartId.SeoCampaigns:
                    return `${item.quantity} ${translate("addons.cart.tooltip.text.campaigns")}`;
                case AddOnsCartId.SeoKeywords:
                    return `${item.quantity} ${translate("addons.cart.tooltip.text.keywords")}`;
                case AddOnsCartId.CountryFilter: {
                    const countryFilterText = translate("addons.cart.tooltip.text.countryFilter");
                    const countryText = translate(`common.country.${item.countryId}`);
                    return `${countryFilterText} ${countryText}`;
                }
                case AddOnsCartId.MonitorAndProtectCrawls:
                    return `${item.quantity} ${translate("addons.cart.tooltip.text.crawls")}`;
                default:
                    return "";
            }
        });
    }, [cartItems]);

    const onCartClick = useCallback(() => {
        const packageIds = cartItems.map((item) => {
            if (item.id === AddOnsCartId.HistoricalData) {
                return `${item.quantity}Months`;
            }

            if (item.id === AddOnsCartId.CountryFilter) {
                return `${item.id}${item.countryAlpha2Code}`;
            }

            return `${item.id}${item.quantity}`;
        });

        if (packageIds.length !== 1) {
            goToAddonsPage({ packageIds: packageIds });
            return;
        }

        const singleAddon = cartItems[0];
        const addonTypeName = cartIdToAddonTypeNameMap[singleAddon.id];
        const variation = singleAddon.id === AddOnsCartId.CountryFilter ? singleAddon.countryAlpha3Code : undefined;

        handlePurchaseAddonClick(addonTypeName, singleAddon.quantity, variation);
    }, [cartItems, handlePurchaseAddonClick]);

    const isAddonsCartButtonEnabled = !hasDunnings && isAddonsCartEnabled;

    const hasAddonsCartBanner = useMemo(() => {
        return (
            !!parent &&
            pagesToShowBanner.includes(parent) &&
            cartItems.length > 0 &&
            isAddonsCartBannerDismissed !== "true" &&
            !hasDunnings &&
            isAddonsCartEnabled
        );
    }, [isAddonsCartEnabled, hasDunnings, isAddonsCartBannerDismissed, parent, cartItems]);

    const onCloseBannerClick = useCallback(() => {
        setIsAddonsCartBannerDismissed("true");
    }, [setIsAddonsCartBannerDismissed]);

    const removeAddonFromCart = useCallback(
        (addonType: string): void => {
            if (!addonType) {
                return;
            }

            if (!cartFromCookies?.length) {
                return;
            }

            const addonIndex = cartFromCookies.indexOf(addonType);
            if (addonIndex === -1) {
                return;
            }

            const updatedCart = [...cartFromCookies];
            updatedCart.splice(addonIndex, 1);

            const cookieManager = new CookieManager();
            cookieManager.setCookie(ADDONS_CART_COOKIE_NAME, JSON.stringify(updatedCart));

            setAddonsFromCookies(updatedCart.join(","));
        },
        [cartFromCookies],
    );

    return {
        totalCartItems: cartItems.length,
        cartItemsText,
        hasAddonsCartBanner: hasAddonsCartBanner,
        isAddonsCartButtonEnabled,
        addonsFromCookies,
        onCartClick,
        onCloseBannerClick,
        removeAddonFromCart,
    };
};
